import { createId } from "./id";
import type { Ack, GameState } from "./types";

type Listener = (...args: any[]) => void;

/**
 * An `Ack` plus one client-only flag. `retryable` marks failures that came from
 * the wire rather than from the chamber, so the app can hold on to the session
 * instead of logging the player out over a dropped packet.
 */
export interface AckResult extends Ack {
  retryable?: boolean;
}

type AckCallback = (ack: AckResult) => void;

const API_BASE = String(import.meta.env.VITE_API_URL || "").replace(/\/$/, "");

const RECONNECT_BASE_MS = 800;
const RECONNECT_CEILING_MS = 15000;

export function apiUrl(path: string) {
  return `${API_BASE}${path}`;
}

/**
 * Dispatched with `connect_error`. `auth` is true ONLY when the chamber
 * actively rejected our credentials (HTTP 401/403). Every transport failure —
 * offline, DNS, timeout, 5xx, CORS — reports `auth: false` so the app never
 * throws away a stored password because the tunnel wobbled.
 */
export class ConnectError extends Error {
  readonly auth: boolean;
  readonly status: number;

  constructor(message: string, auth: boolean, status = 0) {
    super(message);
    this.name = "ConnectError";
    this.auth = auth;
    this.status = status;
  }
}

export class GameSocket {
  auth: { sitePassword?: string } = {};
  connected = false;

  private listeners = new Map<string, Set<Listener>>();
  private roomSocket: WebSocket | null = null;
  private siteTicket = "";
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private generation = 0;
  private manuallyClosed = true;
  private pending = new Map<string, AckCallback>();

  on(event: string, listener: Listener) {
    const listeners = this.listeners.get(event) ?? new Set<Listener>();
    listeners.add(listener);
    this.listeners.set(event, listeners);
    return this;
  }

  removeAllListeners() {
    this.listeners.clear();
    return this;
  }

  async connect() {
    this.manuallyClosed = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    try {
      const response = await fetch(apiUrl("/api/connect"), {
        method: "POST",
        headers: this.authHeaders()
      });
      const result = (await response.json().catch(() => ({}))) as {
        ok?: boolean;
        ticket?: string;
        error?: string;
      };
      if (!response.ok || !result.ok || !result.ticket) {
        const rejected = response.status === 401 || response.status === 403;
        throw new ConnectError(
          result.error || "The chamber refused entry.",
          rejected,
          response.status
        );
      }
      this.siteTicket = result.ticket;
      this.reconnectAttempts = 0;
      this.connected = true;
      this.dispatch("connect");
    } catch (error) {
      this.connected = false;
      const failure =
        error instanceof ConnectError
          ? error
          : new ConnectError(
              error instanceof Error ? error.message : "The chamber could not be reached.",
              false
            );
      this.dispatch("connect_error", failure);
      // A rejected passphrase will never fix itself; anything else might.
      if (!failure.auth) this.scheduleReconnect();
    }
  }

  disconnect() {
    this.manuallyClosed = true;
    this.reconnectAttempts = 0;
    this.generation += 1;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.roomSocket;
    this.roomSocket = null;
    socket?.close(1000, "Leaving room");
    this.rejectPending("The chamber connection closed.");
    if (this.connected) {
      this.connected = false;
      this.dispatch("disconnect");
    }
  }

  emit(event: string, payload: unknown = {}, callback?: AckCallback) {
    if (event === "room:create" || event === "room:join") {
      void this.enterRoom(event, payload, callback);
      return this;
    }

    if (!this.roomSocket || this.roomSocket.readyState !== WebSocket.OPEN) {
      callback?.({ ok: false, error: "Reconnect to the chamber first.", retryable: true });
      return this;
    }

    const id = callback ? createId() : undefined;
    if (id && callback) this.pending.set(id, callback);
    this.roomSocket.send(JSON.stringify({ id, type: event, payload }));
    return this;
  }

  private async enterRoom(event: string, payload: unknown, callback?: AckCallback) {
    try {
      const body = (payload ?? {}) as Record<string, unknown>;
      const code = String(body.code || "").trim().toUpperCase();
      const endpoint =
        event === "room:create"
          ? "/api/rooms"
          : `/api/rooms/${encodeURIComponent(code)}/join`;
      const response = await fetch(apiUrl(endpoint), {
        method: "POST",
        headers: {
          ...this.authHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const result = (await response.json().catch(() => ({}))) as AckResult;
      if (!response.ok || !result.ok || !result.code || !result.playerToken) {
        callback?.({
          ok: false,
          error: result.error || "Could not enter the chamber.",
          // 4xx is the chamber saying no. 5xx / unparseable is the wire.
          retryable: response.status >= 500 || response.status === 0
        });
        return;
      }

      await this.openRoomSocket(result.code, result.playerToken);
      callback?.(result);
    } catch (error) {
      callback?.({
        ok: false,
        error: error instanceof Error ? error.message : "Could not enter the chamber.",
        retryable: true
      });
    }
  }

  private openRoomSocket(code: string, playerToken: string) {
    const generation = ++this.generation;
    this.roomSocket?.close(1000, "Changing room");
    this.roomSocket = null;

    const endpoint = new URL(
      apiUrl(`/api/rooms/${encodeURIComponent(code)}/socket`),
      window.location.href
    );
    endpoint.protocol = endpoint.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(endpoint, [
      "veiled-court",
      `site.${this.siteTicket}`,
      `player.${playerToken}`
    ]);
    this.roomSocket = socket;

    return new Promise<void>((resolve, reject) => {
      let opened = false;
      socket.addEventListener("open", () => {
        if (generation !== this.generation) return;
        opened = true;
        this.reconnectAttempts = 0;
        this.connected = true;
        resolve();
      });
      socket.addEventListener("message", (event) => {
        if (generation !== this.generation) return;
        this.receive(event.data);
      });
      socket.addEventListener("error", () => {
        if (!opened) reject(new Error("The room connection failed."));
      });
      socket.addEventListener("close", () => {
        if (generation !== this.generation) return;
        this.roomSocket = null;
        this.rejectPending("The chamber connection closed.");
        if (!opened) reject(new Error("The room connection failed."));
        if (this.connected) {
          this.connected = false;
          this.dispatch("disconnect");
        }
        this.scheduleReconnect();
      });
    });
  }

  private receive(raw: string | ArrayBuffer | Blob) {
    if (typeof raw !== "string") return;
    try {
      const message = JSON.parse(raw) as {
        id?: string;
        type?: string;
        payload?: Ack | GameState | unknown;
      };
      if (message.type === "ack" && message.id) {
        const callback = this.pending.get(message.id);
        this.pending.delete(message.id);
        callback?.(message.payload as Ack);
        return;
      }
      if (message.type) this.dispatch(message.type, message.payload);
    } catch {
      // Ignore malformed server messages.
    }
  }

  /**
   * Exponential backoff, 800ms -> 15s ceiling, with jitter so a whole table
   * coming back from a tunnel does not stampede the worker in lockstep.
   */
  private scheduleReconnect() {
    if (this.manuallyClosed || this.reconnectTimer !== null) return;
    const step = Math.min(this.reconnectAttempts, 5);
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** step, RECONNECT_CEILING_MS);
    const jitter = Math.round(Math.random() * 260);
    this.reconnectAttempts += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay + jitter);
  }

  private authHeaders(): Record<string, string> {
    const password = String(this.auth.sitePassword || "");
    return password ? { Authorization: `Bearer ${password}` } : {};
  }

  private rejectPending(error: string) {
    for (const callback of this.pending.values()) {
      callback({ ok: false, error, retryable: true });
    }
    this.pending.clear();
  }

  private dispatch(event: string, ...args: unknown[]) {
    for (const listener of this.listeners.get(event) ?? []) {
      listener(...args);
    }
  }
}
